/**
 * CanvasMinimap.tsx
 * Mini-map (vue d'ensemble) pour la navigation dans le canvas
 * Affiche une vue réduite du canvas avec un rectangle indiquant la zone visible
 */
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Stage, Layer, Rect, Group } from 'react-konva';
import Konva from 'konva';
import { useAppStore } from '../../../services/StateService';

interface CanvasMinimapProps {
  stageRef: React.RefObject<Konva.Stage | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
  onViewportClick?: (x: number, y: number) => void;
}

/**
 * Composant Mini-map pour la navigation
 */
export function CanvasMinimap({
  stageRef,
  containerRef,
  onViewportClick,
}: CanvasMinimapProps): React.ReactElement | null {
  const minimapStageRef = useRef<Konva.Stage>(null);
  const [viewportRect, setViewportRect] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [_isDragging, setIsDragging] = useState(false);

  const templateWidth = useAppStore(state => state.canvas.templateWidth);
  const templateHeight = useAppStore(state => state.canvas.templateHeight);
  const shapes = useAppStore(state => state.shapes);
  // P0.2 FIX: Utiliser selection.selectedIds au lieu de canvas.selectedNodeIds
  const selectedNodeIds = useAppStore(state => state.selection.selectedIds);
  const zoomLevel = useAppStore(state => state.canvas.zoomLevel);

  // Taille de la mini-map
  const MINIMAP_SIZE = 200;
  const MINIMAP_PADDING = 10;

  // Calculer le scale pour afficher tout le canvas dans la mini-map
  const scale = Math.min(
    (MINIMAP_SIZE - MINIMAP_PADDING * 2) / templateWidth,
    (MINIMAP_SIZE - MINIMAP_PADDING * 2) / templateHeight
  );

  // Mettre à jour le rectangle de viewport
  const updateViewportRect = useCallback(() => {
    const stage = stageRef.current;
    const container = containerRef.current;
    if (!stage || !container) return;

    const containerRect = container.getBoundingClientRect();
    const stagePos = stage.position();
    const stageScale = stage.scaleX();

    // Calculer la zone visible dans le canvas
    const viewportX = -stagePos.x / stageScale;
    const viewportY = -stagePos.y / stageScale;
    const viewportWidth = containerRect.width / stageScale;
    const viewportHeight = containerRect.height / stageScale;

    // Convertir en coordonnées de la mini-map
    const minimapX = MINIMAP_PADDING + viewportX * scale;
    const minimapY = MINIMAP_PADDING + viewportY * scale;
    const minimapWidth = viewportWidth * scale;
    const minimapHeight = viewportHeight * scale;

    setViewportRect({
      x: minimapX,
      y: minimapY,
      width: minimapWidth,
      height: minimapHeight,
    });
  }, [stageRef, containerRef, scale]);

  // Mettre à jour le viewport quand le stage change (remplace setInterval par événements)
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    // Écouter les changements de position du stage (remplace le polling)
    const handleStageChange = () => {
      updateViewportRect();
    };

    // NETTOYÉ: Écouter seulement les événements nécessaires pour la minimap
    // dragmove et wheel suffisent (dragend et mousemove sont redondants)
    stage.on('dragmove', handleStageChange);
    stage.on('wheel', handleStageChange);

    // P3 : zustand v4 — signature subscribe(state => …) avec diff manuel.
    let lastZoom: number | undefined;
    const unsubscribe = useAppStore.subscribe(state => {
      const z = state.canvas.zoomLevel;
      if (z !== lastZoom) {
        lastZoom = z;
        updateViewportRect();
      }
    });

    // Mise à jour initiale
    updateViewportRect();

    return () => {
      unsubscribe();
      // NETTOYÉ: Cleanup des listeners (dragend et mousemove supprimés - redondants)
      if (stage) {
        stage.off('dragmove', handleStageChange);
        stage.off('wheel', handleStageChange);
      }
    };
  }, [updateViewportRect, stageRef]);

  // Initialiser le viewport
  useEffect(() => {
    updateViewportRect();
  }, [updateViewportRect, zoomLevel]);

  // Handler pour cliquer sur la mini-map pour centrer la vue
  const handleMinimapClick = useCallback(
    (_e: Konva.KonvaEventObject<MouseEvent>) => {
      const stage = stageRef.current;
      const minimapStage = minimapStageRef.current;
      if (!stage || !minimapStage) return;

      const pointer = minimapStage.getPointerPosition();
      if (!pointer) return;

      // Convertir les coordonnées de la mini-map en coordonnées du canvas
      const canvasX = (pointer.x - MINIMAP_PADDING) / scale;
      const canvasY = (pointer.y - MINIMAP_PADDING) / scale;

      // Centrer la vue sur ce point
      const container = containerRef.current;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const newStageX = -(canvasX * zoomLevel - containerRect.width / 2);
      const newStageY = -(canvasY * zoomLevel - containerRect.height / 2);

      stage.position({ x: newStageX, y: newStageY });
      stage.batchDraw();

      if (onViewportClick) {
        onViewportClick(canvasX, canvasY);
      }
    },
    [stageRef, containerRef, scale, zoomLevel, onViewportClick]
  );

  // Handler pour drag du viewport rectangle
  const handleViewportDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleViewportDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleViewportDragMove = useCallback(
    (e: Konva.KonvaEventObject<DragEvent>) => {
      if (!viewportRect) return;

      const stage = stageRef.current;
      const container = containerRef.current;
      if (!stage || !container) return;

      const node = e.target as Konva.Rect;
      const newX = node.x();
      const newY = node.y();

      // Convertir en coordonnées du canvas
      const canvasX = (newX - MINIMAP_PADDING) / scale;
      const canvasY = (newY - MINIMAP_PADDING) / scale;

      // Mettre à jour la position du stage
      const containerRect = container.getBoundingClientRect();
      const newStageX = -(canvasX * zoomLevel - containerRect.width / 2);
      const newStageY = -(canvasY * zoomLevel - containerRect.height / 2);

      stage.position({ x: newStageX, y: newStageY });
      stage.batchDraw();
    },
    [viewportRect, stageRef, containerRef, scale, zoomLevel]
  );

  // Rendre les formes simplifiées pour la mini-map
  const renderMinimapShapes = () => {
    return shapes.map(shape => {
      const isSelected = selectedNodeIds.includes(shape.id);
      const x = MINIMAP_PADDING + (shape.x || 0) * scale;
      const y = MINIMAP_PADDING + (shape.y || 0) * scale;
      const width = (shape.width || 100) * scale;
      const height = (shape.height || 100) * scale;

      return (
        <Rect
          key={shape.id}
          x={x}
          y={y}
          width={width}
          height={height}
          fill={isSelected ? '#3b82f6' : '#6b7280'}
          opacity={isSelected ? 0.8 : 0.3}
          stroke={isSelected ? '#3b82f6' : 'transparent'}
          strokeWidth={1}
          listening={false}
        />
      );
    });
  };

  return (
    <div
      style={{
        position: 'absolute',
        bottom: '20px',
        right: '20px',
        width: MINIMAP_SIZE,
        height: MINIMAP_SIZE,
        backgroundColor: 'var(--color-bg-secondary, #f8fafc)',
        border: '1px solid var(--color-border, #e2e8f0)',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        zIndex: 1000,
        overflow: 'hidden',
      }}
    >
      <Stage width={MINIMAP_SIZE} height={MINIMAP_SIZE} ref={minimapStageRef}>
        <Layer>
          {/* Fond du canvas */}
          <Rect
            x={MINIMAP_PADDING}
            y={MINIMAP_PADDING}
            width={templateWidth * scale}
            height={templateHeight * scale}
            fill="#ffffff"
            stroke="#e2e8f0"
            strokeWidth={1}
            listening={false}
          />

          {/* Formes simplifiées */}
          <Group listening={false}>{renderMinimapShapes()}</Group>

          {/* Rectangle de viewport (zone visible) */}
          {viewportRect && (
            <Rect
              x={viewportRect.x}
              y={viewportRect.y}
              width={viewportRect.width}
              height={viewportRect.height}
              fill="rgba(59, 130, 246, 0.1)"
              stroke="#3b82f6"
              strokeWidth={2}
              dash={[4, 4]}
              draggable
              onDragStart={handleViewportDragStart}
              onDragMove={handleViewportDragMove}
              onDragEnd={handleViewportDragEnd}
              onMouseDown={handleMinimapClick}
            />
          )}
        </Layer>
      </Stage>
    </div>
  );
}

export default CanvasMinimap;
